import './styles/style.scss';

import baseConfig from '@actr/config';
import mainContainer, { initDI, loadDIModules, useInject } from '@actr/di';
import { useNavigation } from '@actr/hooks/src/navigation/hook';
import { useIsClient } from '@actr/hooks/src/useIsClient';
import { LangToLocale, Locales, LocaleToLang } from '@actr/specs/src/locales';
import activityDIModule from '@actr/stores/src/modules/account/activity';
import findMyOrderDIModule from '@actr/stores/src/modules/account/find-my-order';
import appDIModule from '@actr/stores/src/modules/common/app';
import channelConfigDIModule from '@actr/stores/src/modules/common/channel-config';
import marketingSubscriptionDIModule from '@actr/stores/src/modules/common/marketing-subscription';
import userInfoDIModule from '@actr/stores/src/modules/common/user-info';
import { AppStore } from '@actr/stores/src/stores/stores/common/app';
import { DeviceSizes } from '@actr/style';
import { AKVanillaThemeProvider } from '@actr/style/src/providers/vanilla-theme-provider';
import { UiContext } from '@actr/ui/src/contexts/ui-context';
import { canUseDom } from '@actr/utilities/src/dom/index';
import { getStorage, STORAGES } from '@actr/utilities/src/local-storage';
import { saveAdsData } from '@actr/utilities/src/url/ad-checker';
import { saveAdmitad } from '@actr/utilities/src/url/admitad';
import { saveClickId } from '@actr/utilities/src/url/click-id';
import AppVersionNotifier from '@actr/widgets/src/components/_common/app-version-notifier';
import { AppVersionUpdateMode } from '@actr/widgets/src/components/_common/app-version-notifier/specs';
import CookieNotifier from '@actr/widgets/src/components/_common/cookie-notifier';
import OpenInAppPanel from '@actr/widgets/src/components/_common/open-in-app-panel';
import { ScrollToTop } from '@actr/widgets/src/components/_common/scroll-to-top';
import _ApiSwitcher from '@actr/widgets/src/components/_misc/api-switcher';
import { AirlineProvider } from '@actr/widgets/src/components/_misc/app-installation-buttons/context';
import { ErrorBoundary } from '@actr/widgets/src/components/_misc/error-boundary';
import { SiteLoader } from '@actr/widgets/src/components/_misc/site-loader';
import { StickyRightButtons } from '@actr/widgets/src/components/_misc/sticky-right-buttons';
import { FeedbackPanel } from '@actr/widgets/src/components/feedback/panel';
import { WidgetsContext } from '@actr/widgets/src/contexts/widgets';
import { MetricsHoc } from '@actr/widgets/src/hoc/metrics';
import { useAuthSession } from '@actr/widgets/src/hooks/use-auth-session';
import { UseFetchChannelConfig } from '@actr/widgets/src/hooks/use-fetch-channel-config';
import { AkV2Routes } from '@actr/widgets/src/navigation/routes/ak-v2';
// eslint-disable-next-line import/no-unresolved
import ActrRouter from 'actr-router';
import PageHead from 'Components/_misc/page-head';
import ContentWrapper from 'Components/hoc/content-wrapper';
import Footer from 'Components/layouts/footer';
import config from 'Configs';
import { createBrowserHistory, createMemoryHistory } from 'history';
import { observer } from 'mobx-react-lite';
import localAuthDIModule from 'Modules/override/auth';
import type { FC } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { Router } from 'react-router';
import ProjectUtils from 'Root/utils/project';

import useCommonTranslator from './locales';
import * as Styled from './styles/style.css';

let ApiSwitcher: FC;

// такая логина нужна, что бы исключить из бандла код, который не должен попасть в бандл
if (import.meta.env.ACTR_ENABLE_API_SWITCHER) {
  ApiSwitcher = _ApiSwitcher;
}

const App: React.FC = observer(() => {
  const app = useInject(AppStore);
  const { t } = useCommonTranslator();
  const { searchParams: { lng, locale } } = useNavigation();

  const history = useMemo(() => canUseDom ? createBrowserHistory() : createMemoryHistory(), [canUseDom]);

  const isClient = useIsClient();

  useEffect(() => {
    document.title = t('Поиск выгодных предложений');
  }, [app.locale, t]);

  // временный костыль, что бы локально тема из ЛС не затирала из конфига
  useEffect(() => {
    app.setTheme(config.theme);
  }, []);

  useEffect(() => {
    const cookieLocale = getStorage('i18next', [STORAGES.COOKIE], '', { raw: true });
    if (baseConfig.app.localeSelectEnable && lng && Object.values(Locales).includes(lng) && app.locale !== lng) {
      app.setLocale(lng);
    } else if (baseConfig.app.localeSelectEnable
      && locale
      && Object.values(LocaleToLang).includes(locale)
      && app.locale !== LangToLocale[locale]) {
      app.setLocale(LangToLocale[locale]);
    } else if (baseConfig.app.localeSelectEnable && cookieLocale) {
      app.setLocale(cookieLocale);
    }
    saveAdmitad();
    saveAdsData();
    saveClickId();
  }, []);

  useEffect(() => {
    if (getStorage('cookieAccepted', [STORAGES.COOKIE])) {
      app.setIsCookieAccepted(true);
    }
  }, [getStorage('cookieAccepted', [STORAGES.COOKIE])]);

  UseFetchChannelConfig();
  useAuthSession();

  const handleUpdateDeviceSize = useCallback((deviceSize: DeviceSizes) => {
    app.setDeviceSize(deviceSize);
  }, []);

  const handleError = useCallback((error: Error | null) => {
    if (error) {
      app.addError(error);
    }
  }, []);

  function renderRouter() {
    return <ActrRouter />;
  }

  function renderContent() {
    return (
      <>
        <ScrollToTop excludeRoutes={[AkV2Routes.AVIA_SEARCH, AkV2Routes.AVIA_RESULTS]} />
        <PageHead />
        <div
          className={Styled.Content({
            disableMarginForFooterSpacing: ProjectUtils.isSkb(),
            disableFlexGrow: ProjectUtils.isSkb(),
            isOtello: ProjectUtils.isOtello()
          })}
          id="siteMainContent">
          {renderRouter()}
        </div>
        {!ProjectUtils.isSkb() && (
          <Footer />
        )}
        {!ProjectUtils.isWebview() && <StickyRightButtons disableUpButton={ProjectUtils.isSkb()} />}
        <FeedbackPanel />
        {!app.isCookieAccepted && ProjectUtils.isAkRedesign() && (
          <CookieNotifier
            isNeedAdditionalBottom={!!ApiSwitcher}
          />
        )}
        <OpenInAppPanel />
        <AppVersionNotifier mode={AppVersionUpdateMode.AUTO} />
      </>
    );
  }

  return (
    <Router history={history}>
      <WidgetsContext.Provider value={{
        excludedPayments: config.payment.excludedPayments,
        projectType: config.projectType
      }}>
        <UiContext.Provider value={{
          deviceSize: app.deviceSize,
          popoverContainerElement: canUseDom ? (document.getElementById('popover-container') || document.body) : null,
          modalContainerElement: canUseDom ? (document.getElementById('modal-container') || document.body) : null,
        }}>
          <AirlineProvider>
            <AKVanillaThemeProvider currentTheme={config.theme} onUpdateDeviceSize={handleUpdateDeviceSize}>
              <MetricsHoc>
                <ContentWrapper>
                  <ErrorBoundary local={ProjectUtils.isSber()} onReactError={handleError}>
                    {isClient ? renderContent() : <SiteLoader />}
                  </ErrorBoundary>
                </ContentWrapper>
              </MetricsHoc>
              {ApiSwitcher && <ApiSwitcher />}
            </AKVanillaThemeProvider>
          </AirlineProvider>
        </UiContext.Provider>
      </WidgetsContext.Provider>
    </Router>
  );
});

export default initDI(loadDIModules(
  App,
  appDIModule,
  localAuthDIModule,
  activityDIModule,
  channelConfigDIModule,
  marketingSubscriptionDIModule,
  userInfoDIModule,
  findMyOrderDIModule
), mainContainer);
